import React, { useEffect, useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import {
  ROLES_SIXT,
  ROLE_B2C,
  ROLE_PARTNER_READ_ONLY,
  ROLE_READ_ONLY,
  useAuth,
} from 'auth'

import PageLoading from 'components/atoms/PageLoading'
import CarsToBuyList from 'components/organisms/CarsToBuy'
import useCarsToBuySession from 'components/organisms/CarsToBuy/useCarsToBuySession'
import useCarsToBuy from 'components/organisms/CarsToBuy/useCarsToBuy'
import NotFound from 'components/atoms/NotFound'
import { getCarsToBuyUrl } from 'components/organisms/CarsToBuy/carsToBuyLib'

import useTimer from 'hooks/useTimer'
import useEntityData from 'hooks/useEntityData'

export default function CarsToBuy() {
  const { getPartnerId } = useAuth()

  const { hasOneOfRoles } = useAuth()

  const admin = useMemo(() => hasOneOfRoles(ROLES_SIXT), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () => hasOneOfRoles([ROLE_READ_ONLY, ROLE_B2C, ROLE_PARTNER_READ_ONLY]),
    [hasOneOfRoles]
  )

  const { partnerId } = useParams()

  const { isFetching, fetchSession, session, error } =
    useCarsToBuySession('current')

  const {
    isFetching: isFetchingPrevious,
    fetchSession: fetchPreviousSession,
    session: previousSession,
  } = useCarsToBuySession('previous')

  const {
    items: partners,
    isFetching: isFetchingPartners,
    refetch: fetchPartners,
  } = useEntityData({ limit: 9999 })

  const {
    isFetching: isFetchingCars,
    isUpdating: isUpdatingCars,
    fetchCarsToBuy,
    updateChoice,
    addMessage,
    cars,
    error: errorCarsToBuy,
  } = useCarsToBuy()

  const { countDown, initializeTimer } = useTimer(
    session?.startsAt,
    session?.endsAt
  )

  useEffect(() => {
    if (!admin) {
      return
    }

    fetchPartners(`partners/`)
  }, [admin, fetchPartners])

  const myPartnerId = useMemo(
    () =>
      admin
        ? partnerId &&
          (partners.length === 0 ||
            partners.find((partner) => partner.partnerId === partnerId))
          ? partnerId
          : partners?.[0]?.partnerId
        : getPartnerId(),
    [admin, getPartnerId, partnerId, partners]
  )

  useEffect(() => {
    if (
      !myPartnerId ||
      (session?.sessionId && session?.partnerId === myPartnerId)
    ) {
      return
    }

    fetchSession(myPartnerId)
  }, [
    fetchSession,
    myPartnerId,
    session?.sessionId,
    countDown.expired,
    session?.partnerId,
  ])

  useEffect(() => {
    if (session?.sessionId) {
      initializeTimer()
    }
  }, [initializeTimer, session?.sessionId])

  useEffect(() => {
    if (!myPartnerId) {
      return
    }

    fetchPreviousSession(myPartnerId, true)
  }, [fetchPreviousSession, myPartnerId])

  useEffect(() => {
    if (!session?.sessionId || !myPartnerId) {
      return
    }

    fetchCarsToBuy(session?.sessionId, myPartnerId)
  }, [fetchCarsToBuy, myPartnerId, session?.sessionId])

  const readOnly = useMemo(
    () => isFetchingCars || isFetching || isUpdatingCars,
    [isFetchingCars, isFetching, isUpdatingCars]
  )

  return (
    <div className="content-container">
      {(isFetchingCars && !cars.length) ||
      isFetching ||
      isFetchingPrevious ||
      isFetchingPartners ? (
        <PageLoading />
      ) : (error && Number(error?.response?.status) === 404) ||
        (errorCarsToBuy && Number(errorCarsToBuy?.response?.status) === 404) ? (
        <NotFound />
      ) : myPartnerId !== partnerId ? (
        <Navigate to={getCarsToBuyUrl(myPartnerId)} />
      ) : (
        <CarsToBuyList
          session={session}
          previousSession={previousSession}
          admin={admin}
          partnerId={myPartnerId}
          partners={partners}
          updateChoice={updateChoice}
          addMessage={addMessage}
          cars={cars}
          readOnly={readOnly}
          contentIsReadOnly={userRoleHasReadOnly}
          countDown={countDown}
        />
      )}
    </div>
  )
}
